import React, { useEffect, useRef, useState } from 'react';
import {
  Drawer, Typography, Box, Tooltip, Switch,
  useTheme, useMediaQuery,
} from '@mui/material';
import {
  SpaceDashboardOutlined, InsightsOutlined, LocalShippingOutlined, DirectionsCarOutlined,
  Inventory2Outlined, SensorsOutlined, LocalGasStationOutlined, WaterDropOutlined,
  ShieldOutlined, NotificationsNoneOutlined, AssessmentOutlined, MapOutlined,
  HistoryOutlined, HubOutlined, GroupOutlined, SettingsOutlined,
  MoreHoriz, KeyboardDoubleArrowLeft, KeyboardDoubleArrowRight, ExpandLess,
} from '@mui/icons-material';
// Logo trimmed of its transparent padding, and the pin on its own for the collapsed rail.
import Logo from '../../../assets/LogoTrim.png';
import LogoMark from '../../../assets/LogoMark.png';
import { AccessContext, canAccessPath } from '../../../utils/permissions';

/* ── Sidebar ─────────────────────────────────────────────────────────────
   A quiet navy rail: the pages people use every day in short groups split by
   hairlines, the rest behind "Show more", and Collapse / "Show all modules" at
   the foot. Order inside the groups follows the operations team's order. */

interface SidebarItem {
  id: string;
  title: string;
  icon: React.ReactNode;
  path: string;
}

export interface SidebarQuickStats {
  activeSites:       number;
  runningGenerators: number;
  activeAlerts:      number;
}

interface SidebarProps {
  open: boolean;
  onClose: () => void;
  currentPath?: string;
  variant?: 'permanent' | 'temporary';
  onNavigate?: (path: string) => void;
  /** Sign-out lives in the header's account menu; kept for compatibility. */
  onLogout?: () => void;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  /** No longer shown — the Dashboard carries these figures. Kept for compatibility. */
  quickStats?: SidebarQuickStats;
  /** Identity used to hide nav entries the user cannot open. */
  access?: AccessContext;
}

export const SIDEBAR_WIDTH           = 248;
export const SIDEBAR_COLLAPSED_WIDTH = 68;

/* ── Palette: the app's dark navy rail ── */
const SURFACE  = '#0F172A';
const DIVIDER  = 'rgba(255,255,255,0.08)';
const HOVER    = 'rgba(255,255,255,0.06)';
const ACTIVE   = 'rgba(99,155,230,0.16)';
const ACCENT   = 'rgba(99,155,230,1)';
const TEXT     = '#E2E8F0';
const MUTED    = 'rgba(255,255,255,0.45)';

/* Everyday pages, in groups. */
const GROUPS: SidebarItem[][] = [
  [
    { id: 'dashboard',      title: 'Dashboard',           icon: <SpaceDashboardOutlined />,  path: '/dashboard' },
    { id: 'analytics',      title: 'Analytics',           icon: <InsightsOutlined />,        path: '/analytics' },
  ],
  [
    { id: 'tankers',        title: 'Tanker Fleet',        icon: <LocalShippingOutlined />,   path: '/sites/tankers' },
    { id: 'drivers',        title: 'Driver Monitoring',   icon: <DirectionsCarOutlined />,   path: '/sites/drivers' },
  ],
  [
    { id: 'infrastructure', title: 'Site Infrastructure', icon: <Inventory2Outlined />,      path: '/sites/infrastructure' },
    { id: 'devices',        title: 'Device Management',   icon: <SensorsOutlined />,         path: '/devices' },
  ],
  [
    { id: 'fuel',           title: 'Fuel Issues',         icon: <LocalGasStationOutlined />, path: '/fuel' },
    { id: 'fuel-quality',   title: 'Fuel Quality',        icon: <WaterDropOutlined />,       path: '/monitoring/fuel' },
  ],
  [
    { id: 'security',       title: 'Security & Theft',    icon: <ShieldOutlined />,          path: '/security' },
    { id: 'alerts',         title: 'Alerts',              icon: <NotificationsNoneOutlined />, path: '/alerts' },
    { id: 'reports',        title: 'Reports',             icon: <AssessmentOutlined />,      path: '/reports' },
  ],
];

/* Less-used pages: behind "Show more" unless "Show all modules" is on. */
const MORE: SidebarItem[] = [
  { id: 'site-map', title: 'Site Map',          icon: <MapOutlined />,      path: '/sites/map' },
  { id: 'logs',     title: 'Logs',              icon: <HistoryOutlined />,  path: '/logs' },
  { id: 'users',    title: 'Users & Suppliers', icon: <GroupOutlined />,    path: '/users' },
  { id: 'sap',      title: 'SAP Integration',   icon: <HubOutlined />,      path: '/sap' },
  { id: 'settings', title: 'Settings',          icon: <SettingsOutlined />, path: '/settings' },
];

const ALL_MODULES_KEY = 'pt360.sidebar.showAllModules';
const readAllModules = () => { try { return localStorage.getItem(ALL_MODULES_KEY) === '1'; } catch { return false; } };
const writeAllModules = (v: boolean) => { try { localStorage.setItem(ALL_MODULES_KEY, v ? '1' : '0'); } catch { /* private mode */ } };

const FADES = {
  top:    'linear-gradient(to bottom, transparent 0, #000 24px)',
  bottom: 'linear-gradient(to top, transparent 0, #000 24px)',
  both:   'linear-gradient(to bottom, transparent 0, #000 24px, #000 calc(100% - 24px), transparent 100%)',
} as const;

const allowed = (items: SidebarItem[], access?: AccessContext) =>
  access ? items.filter(i => canAccessPath(access, i.path)) : [];

/* ── One row ── */
const Row: React.FC<{
  icon: React.ReactNode; label: React.ReactNode; active?: boolean; collapsed?: boolean; tooltip?: string;
  onClick?: () => void; trailing?: React.ReactNode; muted?: boolean;
}> = ({ icon, label, active, collapsed, tooltip, onClick, trailing, muted }) => {
  const row = (
    <Box component="button" type="button" onClick={onClick} aria-current={active ? 'page' : undefined}
      sx={{
        all: 'unset', boxSizing: 'border-box', cursor: 'pointer',
        display: 'flex', alignItems: 'center', gap: 1.5,
        width: collapsed ? 40 : '100%', height: 40, mx: collapsed ? 'auto' : 0, flexShrink: 0,
        px: collapsed ? 0 : 1.5, justifyContent: collapsed ? 'center' : 'flex-start',
        borderRadius: '12px',
        bgcolor: active ? ACTIVE : 'transparent',
        color: active ? ACCENT : muted ? MUTED : TEXT,
        transition: 'background-color .15s, color .15s',
        '&:hover': { bgcolor: active ? ACTIVE : HOVER },
        '&:focus-visible': { outline: `2px solid ${ACCENT}`, outlineOffset: 1 },
        '& svg': { fontSize: 20, flexShrink: 0 },
      }}>
      {icon}
      {!collapsed && (
        <Typography component="span" noWrap sx={{ flex: 1, minWidth: 0, fontSize: '0.88rem', fontWeight: active ? 600 : 500, color: 'inherit', letterSpacing: '-0.005em' }}>
          {label}
        </Typography>
      )}
      {!collapsed && trailing}
    </Box>
  );
  return collapsed ? <Tooltip title={tooltip ?? (typeof label === 'string' ? label : '')} placement="right" arrow>{row}</Tooltip> : row;
};

const Rule: React.FC<{ collapsed?: boolean }> = ({ collapsed }) => (
  <Box sx={{ height: '1px', flexShrink: 0, bgcolor: DIVIDER, mx: collapsed ? 1 : 0, my: 0.75 }} />
);

const SidebarContent: React.FC<{
  currentPath?: string;
  onNavigate?: (path: string) => void;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  showToggle?: boolean;
  access?: AccessContext;
}> = ({ currentPath, onNavigate, collapsed = false, onToggleCollapse, showToggle = true, access }) => {
  const [allModules, setAllModules] = useState(readAllModules);
  const [moreOpen, setMoreOpen] = useState(false);

  const groups = GROUPS.map(g => allowed(g, access)).filter(g => g.length > 0);
  const more = allowed(MORE, access);
  // A page from "more" that is open right now stays visible, so the current page is never hidden.
  const showMore = allModules || moreOpen;
  const pinned = showMore ? [] : more.filter(i => i.path === currentPath);

  const go = (path: string) => onNavigate?.(path);

  /* Fade the top/bottom edge of the list only while there is more to scroll that way. */
  const navRef = useRef<HTMLDivElement>(null);
  const [fade, setFade] = useState<'none' | 'top' | 'bottom' | 'both'>('none');
  const measure = () => {
    const el = navRef.current;
    if (!el) return;
    const up = el.scrollTop > 2;
    const down = el.scrollTop + el.clientHeight < el.scrollHeight - 2;
    setFade(up && down ? 'both' : up ? 'top' : down ? 'bottom' : 'none');
  };
  useEffect(() => {
    measure();
    const el = navRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collapsed, allModules, moreOpen, access]);
  // Bring the current page into view when it changes (e.g. Reports on a short screen).
  useEffect(() => {
    navRef.current?.querySelector('[aria-current="page"]')?.scrollIntoView({ block: 'nearest' });
  }, [currentPath, collapsed]);
  const item = (i: SidebarItem) => (
    <Row key={i.id} icon={i.icon} label={i.title} active={currentPath === i.path} collapsed={collapsed} onClick={() => go(i.path)} />
  );

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: SURFACE, overflow: 'hidden' }}>
      {/* Brand */}
      <Box sx={{ flexShrink: 0, height: 64, display: 'flex', alignItems: 'center', justifyContent: collapsed ? 'center' : 'flex-start', px: collapsed ? 0 : 2.25 }}>
        <Box component="img" src={collapsed ? LogoMark : Logo} alt="PetroTag360" onClick={() => go('/dashboard')}
          sx={{ height: collapsed ? 28 : 38, maxWidth: collapsed ? 28 : 170, objectFit: 'contain', objectPosition: collapsed ? 'center' : 'left center',
                cursor: 'pointer', filter: 'brightness(0) invert(1)' }} />
      </Box>

      {/* Pages — scrolls only on short screens, with no scrollbar: a fade at the edge
          says there is more instead. */}
      <Box component="nav" aria-label="Main" ref={navRef} onScroll={measure} sx={{
        flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', px: collapsed ? 1 : 1.5, pt: 0.25, pb: 0.75,
        scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' },
        ...(fade !== 'none' && {
          maskImage: FADES[fade], WebkitMaskImage: FADES[fade],
        }),
      }}>
        {groups.map((g, gi) => (
          <React.Fragment key={gi}>
            {gi > 0 && <Rule collapsed={collapsed} />}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.125 }}>{g.map(item)}</Box>
          </React.Fragment>
        ))}
        {more.length > 0 && (showMore || pinned.length > 0) && (
          <>
            <Rule collapsed={collapsed} />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.125 }}>{(showMore ? more : pinned).map(item)}</Box>
          </>
        )}
      </Box>

      {/* Foot */}
      <Box sx={{ flexShrink: 0, px: collapsed ? 1 : 1.5, pb: 1 }}>
        {more.length > 0 && !allModules && (
          <>
            <Rule collapsed={collapsed} />
            <Row icon={moreOpen ? <ExpandLess /> : <MoreHoriz />} collapsed={collapsed}
              tooltip={moreOpen ? 'Show less' : `Show more (${more.length})`}
              label={moreOpen ? 'Show less' : <>Show more <Box component="span" sx={{ color: MUTED, fontWeight: 500 }}>({more.length})</Box></>}
              onClick={() => setMoreOpen(v => !v)} />
          </>
        )}
        {showToggle && (
          <>
            <Rule collapsed={collapsed} />
            <Row icon={collapsed ? <KeyboardDoubleArrowRight /> : <KeyboardDoubleArrowLeft />} collapsed={collapsed}
              label="Collapse" tooltip={collapsed ? 'Expand' : 'Collapse'} onClick={onToggleCollapse} />
          </>
        )}
        {!collapsed && more.length > 0 && (
          <Box component="label" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pl: 1.5, pr: 0.25, mt: 0.5, cursor: 'pointer' }}>
            <Typography sx={{ fontSize: '0.8rem', color: MUTED }}>Show all modules</Typography>
            <Switch size="small" checked={allModules}
              onChange={e => { setAllModules(e.target.checked); writeAllModules(e.target.checked); if (!e.target.checked) setMoreOpen(false); }}
              inputProps={{ 'aria-label': 'Show all modules' }}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': { color: '#fff' },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: ACCENT, opacity: 1 },
                '& .MuiSwitch-switchBase:not(.Mui-checked)': { color: '#CBD5E1' },
                '& .MuiSwitch-track': { bgcolor: 'rgba(255,255,255,0.18)', opacity: 1 },
              }} />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export const Sidebar: React.FC<SidebarProps> = ({
  open, onClose, currentPath, onNavigate,
  collapsed = false, onToggleCollapse, access,
}) => {
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const width = collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH;

  if (!isMobile) {
    return (
      <Box sx={{
        position: 'fixed',
        top: 0, left: 0,
        width,
        height: '100vh',
        // The visible viewport on phones/tablets, where browser bars eat into 100vh.
        '@supports (height: 100dvh)': { height: '100dvh' },
        bgcolor: SURFACE,
        borderRight: `1px solid ${DIVIDER}`,
        zIndex: theme.zIndex.drawer,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        transition: 'width 0.22s ease',
      }}>
        <SidebarContent
          currentPath={currentPath}
          onNavigate={onNavigate}
          collapsed={collapsed}
          onToggleCollapse={onToggleCollapse}
          showToggle
          access={access}
        />
      </Box>
    );
  }

  return (
    <Drawer
      variant="temporary"
      open={open}
      onClose={onClose}
      ModalProps={{ keepMounted: true }}
      sx={{
        '& .MuiDrawer-paper': {
          width: SIDEBAR_WIDTH,
          boxSizing: 'border-box',
          border: 'none',
          bgcolor: SURFACE,
        },
      }}
    >
      <SidebarContent
        currentPath={currentPath}
        onNavigate={(path) => { onNavigate?.(path); onClose(); }}
        collapsed={false}
        showToggle={false}
        access={access}
      />
    </Drawer>
  );
};

export default Sidebar;
